import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";
import { formatDate, formatViews } from "@/lib/content";
import NewsCard from "@/components/feature/NewsCard";
import CategoryBadge from "@/components/feature/CategoryBadge";

interface LatestFeedProps {
  items: NewsItem[];
}

export default function LatestFeed({ items }: LatestFeedProps) {
  if (items.length === 0) {
    return (
      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
          最新资讯
        </h2>
        <div className="border border-dashed border-background-300 rounded-lg py-16 text-center">
          <span className="w-12 h-12 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mx-auto">
            <i className="ri-inbox-line text-2xl"></i>
          </span>
          <p className="mt-4 text-foreground-500">暂无最新资讯，稍后再来</p>
        </div>
      </section>
    );
  }

  const [lead, ...rest] = items;

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
            最新资讯
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            按发布时间汇总，影视、音乐与生活内容混合呈现
          </p>
        </div>
        <Link
          to="/search"
          className="hidden sm:inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
        >
          浏览全部
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <Link
        to={`/detail/${lead.id}`}
        className="group animate-fade-up grid grid-cols-1 lg:grid-cols-2 gap-0 bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full h-56 md:h-72 lg:h-full min-h-[220px] overflow-hidden bg-background-200">
          <img
            src={lead.cover}
            alt={`${lead.title} - 小蜜桃最新资讯`}
            title={`${lead.title} - 小蜜桃`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5 md:p-8 flex flex-col justify-center">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryBadge categoryKey={lead.categoryKey} name={lead.categoryName} />
            <span className="text-xs text-foreground-500">{lead.subType}</span>
            <span className="text-xs text-primary-600 font-medium">最新发布</span>
          </div>
          <h3 className="mt-3 font-heading font-bold text-xl md:text-2xl text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {lead.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-foreground-600 line-clamp-3">
            {lead.summary}
          </p>
          <div className="mt-5 flex items-center gap-4 text-xs text-foreground-500">
            <span>{lead.author}</span>
            <span>{formatDate(lead.publishDate)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(lead.views)}
            </span>
          </div>
        </div>
      </Link>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {rest.map((item, idx) => (
          <NewsCard key={item.id} item={item} index={idx} />
        ))}
      </div>
    </section>
  );
}